/** Product-specific gifts are independent of header/picker promotions. */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { isCartPresentationPending, cartPresentationRevision, waitForCartPresentation, registerCartGiftController, suppressAutomaticGiftRefresh } from '@theme/cart-update-coordinator';

const SOURCE = 'item-gift-manager';
let operations = Promise.resolve();
let primaryCheckoutGuard = false;
let browserManager;
let dormant = false;
const memory = new Map();

// Every gift controller shares this queue. Checkout calls each controller only
// outside an operation, so no controller waits on a job queued behind itself.
export function runGiftOperation(task) {
  const operation = operations.catch(() => {}).then(() => {
    const locks = globalThis.navigator?.locks;
    return locks?.request ? locks.request('mm-gift-operation-v1', task) : task();
  });
  operations = operation;
  return operation;
}
export async function waitForGiftOperations() {
  // Drain work already scheduled before a paid-item mutation. Never acquire the
  // same queue here: a queued controller may itself be waiting for the cart.
  while (true) {
    const pending = operations;
    await pending.catch(() => {});
    if (operations === pending) return;
  }
}
export function registerPrimaryGiftGuard() { primaryCheckoutGuard = true; }
export const isItemGift = (item) => typeof item?.properties?._mm_item_gift === 'string' && Boolean(item.properties._mm_item_gift);
export function itemGiftPayable(item) {
  if (!Array.isArray(item?.discount_allocations) || !Number.isFinite(item.original_line_price) || item.original_line_price < 0) return Infinity;
  let amount = 0;
  for (const allocation of item.discount_allocations) {
    if (!Number.isFinite(allocation.amount) || allocation.amount < 0) return Infinity;
    amount += allocation.amount;
  }
  return Math.max(0, item.original_line_price - amount);
}
const configsOf = (cart) => Array.isArray(cart?.configs) ? cart.configs : cart?.config ? [cart.config] : [];
const forOffer = (cart, config) => ({ ...cart, config });
const isPaid = (item) => !item.properties?._gift && !item.properties?._mm_item_gift && !item.properties?._mm_choice_gift;
const rewardsOf = (config) => Array.isArray(config?.rewards) ? config.rewards : [{
  productId: config?.rewardId, available: config?.rewardAvailable,
  variants: [{ id: config?.variantId, available: config?.rewardAvailable }],
}];
const availableReward = (config, variantId, productId) => rewardsOf(config).find((reward) => reward.available === true
  && (productId == null || Number(reward.productId) === Number(productId))
  && reward.variants?.some((variant) => Number(variant.id) === Number(variantId) && variant.available === true));
const validGift = (config, item) => config?.active && config.rewardAvailable
  && availableReward(config, item.variant_id, item.product_id) && item.available === true;
const autoAddAvailable = (config) => (typeof config.autoAddAvailable === 'boolean' ? config.autoAddAvailable : config.rewardAvailable)
  && Boolean(availableReward(config, config.variantId, config.rewardId));
const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
const sellingPlanId = (item) => item.selling_plan_id ?? item.selling_plan_allocation?.selling_plan?.id ?? null;
const settledGiftProperties = (item) => {
  const properties = { ...item.properties };
  if (isItemGift(item)) delete properties._mm_item_gift_attempt;
  return properties;
};
const lineIdentity = (item, properties = item.properties) => JSON.stringify([
  Number(item.product_id), Number(item.variant_id), sellingPlanId(item), propertiesKey(properties),
]);
// Shopify may change keys and merge compatible rows after a property rewrite.
// Compare actual unit totals without assuming any particular returned key.
const settledCartUnits = (cart) => {
  const groups = new Map();
  for (const item of cart.items) {
    const key = lineIdentity(item, settledGiftProperties(item));
    groups.set(key, (groups.get(key) || 0) + item.quantity);
  }
  return JSON.stringify([...groups].sort(([a], [b]) => a.localeCompare(b)));
};
const originalGiftCount = (cart, journal) => cart.items.filter((item) => Number(item.variant_id) === Number(journal.original.variantId)
  && Number(item.product_id) === Number(journal.original.productId) && propertiesKey(item.properties) === propertiesKey(journal.original.properties))
  .reduce((count, item) => count + item.quantity, 0);
const swapStateKey = (cart) => cart.token ? `mm-item-gift-swap-v1:${encodeURIComponent(cart.token)}` : '';
const newAttemptId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
function normalize(cart) {
  if (!Array.isArray(cart?.items) || !configsOf(cart).length) throw Error('Invalid item gift state');
  const items = cart.items.map((item) => {
    const properties = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties || {};
    if (!properties || typeof properties !== 'object' || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || typeof item.key !== 'string') throw Error('Invalid cart line');
    return { ...item, properties };
  });
  const rewardProductIds = new Set(configsOf(cart).flatMap((config) => rewardsOf(config).map((reward) => Number(reward.productId))));
  const offers = new Map(), rewards = new Set(), configs = [];
  for (const source of configsOf(cart)) {
    if (typeof source?.active !== 'boolean' || typeof source.offer !== 'string'
      || !Number.isSafeInteger(source.earned) || source.earned < 0
      || !Number.isSafeInteger(source.qualifyingQuantity) || source.qualifyingQuantity < 0) throw Error('Invalid item gift state');
    const c = { ...source };
    if (offers.has(c.offer)) {
      if (JSON.stringify(offers.get(c.offer)) !== JSON.stringify(source)) throw Error('Conflicting item gift ownership');
      continue;
    }
    offers.set(c.offer, source);
    if (Array.isArray(c.triggerIds)) {
      const triggers = new Set(c.triggerIds.map(Number));
      const quantity = c.active ? items.filter((item) => isPaid(item) && triggers.has(Number(item.product_id))
        && !rewardProductIds.has(Number(item.product_id))).reduce((sum, item) => sum + item.quantity, 0) : 0;
      // Server settings decide entitlement. A gift line can never increase it,
      // even when another offer's reward is also one of these trigger products.
      c.qualifyingQuantity = Math.min(c.qualifyingQuantity, quantity);
      c.earned = Math.min(c.earned, c.qualifyingQuantity);
      const signature = JSON.stringify([[...new Set(rewardsOf(c).map((reward) => Number(reward.productId)))].sort((a, b) => a - b),
        [...triggers].sort((a, b) => a - b)]);
      if (c.active && rewards.has(signature)) { c.active = false; c.earned = 0; }
      else if (c.active) rewards.add(signature);
    }
    configs.push(c);
  }
  return { ...cart, items, configs, config: configs[0] };
}
export function itemGiftRemovals(cart) {
  const offers = new Map(configsOf(cart).map((c) => [c.offer, { config: c, remaining: c.active && c.rewardAvailable ? c.earned : 0 }]));
  const removals = [];
  for (const item of cart.items.filter(isItemGift)) {
    const entry = offers.get(item.properties._mm_item_gift), c = entry?.config;
    const qualifies = validGift(c, item);
    let keep = qualifies ? Math.min(item.quantity, entry.remaining) : 0;
    // Trim a mixed excess line first. Shopify can then make its retained units
    // free; the next fresh pass checks their allocated price again.
    if (keep === item.quantity && itemGiftPayable(item) !== 0) keep = 0;
    if (entry) entry.remaining -= keep;
    if (keep !== item.quantity) removals.push({ key: item.key, quantity: keep });
  }
  return removals;
}
const giftCount = (cart) => cart.items.filter((item) => isItemGift(item) && item.properties._mm_item_gift === cart.config.offer)
  .reduce((count, item) => count + item.quantity, 0);
const stateKey = (cart) => cart.token ? `mm-item-gift-v1:${encodeURIComponent(cart.token)}:${encodeURIComponent(cart.config.offer)}` : '';
const attemptFingerprint = (cart) => JSON.stringify({
  config: cart.config,
  codes: cart.discount_codes || [],
  applications: cart.cart_level_discount_applications || [],
  paid: cart.items.filter(isPaid)
    .map((item) => [item.variant_id, item.quantity, item.original_line_price, item.final_line_price, item.discount_allocations]),
});

export class ItemGiftManager {
  constructor(api) { this.api = api; this.cart = null; this.pending = null; this.pendingDeclines = new Map(); }
  state(cart) {
    const key = stateKey(cart);
    let data = memory.get(key), persistent = Boolean(key);
    try { const saved = this.api.storage?.getItem(key); if (saved) data = JSON.parse(saved); else if (!this.api.storage) persistent = false; }
    catch (_) { persistent = false; }
    const known = data?.version === 1 && Number.isSafeInteger(data.handled) && data.handled >= 0;
    let handled = known ? data.handled : persistent ? 0 : cart.config.earned;
    let failed = known && data.failed && Number.isSafeInteger(data.failed.slots) && data.failed.slots > 0 ? data.failed : null;
    if (failed?.attemptId && cart.config.active && cart.config.rewardAvailable) {
      const recovered = cart.items.filter((item) => isItemGift(item) && item.properties._mm_item_gift === cart.config.offer
        && item.properties._mm_item_gift_attempt === failed.attemptId
        && validGift(cart.config, item) && itemGiftPayable(item) === 0).reduce((sum, item) => sum + item.quantity, 0);
      // A committed POST whose response was lost is a successful reservation
      // once its exact owned line is freshly verified. It must never expire
      // later and revive a gift the shopper deliberately removes.
      if (recovered >= failed.slots) failed = null;
    }
    if (failed && (failed.fingerprint !== attemptFingerprint(cart) || (this.api.now?.() || Date.now()) >= failed.until)) {
      handled = Math.max(0, handled - failed.slots);
      failed = null;
    }
    const current = { version: 1, handled: Math.min(cart.config.earned, Math.max(handled, giftCount(cart))), failed };
    // A remove click can happen before the initial cart request finishes. Its
    // ownership marker carries the offer, and its row carries the removed units.
    const pendingDeclines = this.pendingDeclines.get(cart.config.offer);
    if (pendingDeclines?.size) {
      const declined = [...pendingDeclines.values()].reduce((sum, entry) => sum + entry.quantity, 0);
      current.handled = Math.min(cart.config.earned, Math.max(current.handled, giftCount(cart) + declined));
      if (current.failed?.attemptId && [...pendingDeclines.values()].some((entry) => entry.attemptId === current.failed.attemptId)) current.failed = null;
      this.pendingDeclines.delete(cart.config.offer);
    }
    this.save(key, current);
    return { key, data: current };
  }
  save(key, data) {
    if (!key) return;
    memory.set(key, { ...data });
    try { this.api.storage?.setItem(key, JSON.stringify(data)); } catch (_) { /* Keep the in-page reservation. */ }
  }
  failedAttempt(cart, slots = 1, attemptId = null) {
    const stored = this.state(cart);
    stored.data.failed = {
      fingerprint: attemptFingerprint(cart),
      until: (this.api.now?.() || Date.now()) + 60000,
      slots: Math.min(Math.max(slots, stored.data.failed?.slots || 0), stored.data.handled),
      attemptId,
    };
    this.save(stored.key, stored.data);
  }
  suppress(offer, quantity = 1, attemptId = '', lineKey = '') {
    if (!offer || !Number.isSafeInteger(quantity) || quantity < 1) return;
    const config = configsOf(this.cart).find((c) => c.offer === offer);
    const current = config ? forOffer(this.cart, config) : null;
    if (current && stateKey(current)) {
      const stored = this.state(current);
      stored.data.handled = Math.min(config.earned, Math.max(stored.data.handled, giftCount(current)));
      if (attemptId && stored.data.failed?.attemptId === attemptId) stored.data.failed = null;
      this.save(stored.key, stored.data);
    } else {
      const pending = this.pendingDeclines.get(offer) || new Map();
      const key = attemptId || lineKey || 'unknown';
      pending.set(key, { quantity: Math.max(pending.get(key)?.quantity || 0, quantity), attemptId });
      this.pendingDeclines.set(offer, pending);
    }
  }
  async read() { await this.api.waitForCart(); return normalize(await this.api.readCart()); }
  swapJournal(cart) {
    const key = swapStateKey(cart);
    let journal = memory.get(key);
    try { const saved = this.api.storage?.getItem(key); if (saved) journal = JSON.parse(saved); } catch (_) {}
    return journal?.version === 1 && typeof journal.offer === 'string' && typeof journal.attemptId === 'string'
      && Number.isSafeInteger(journal.originalCount) && journal.originalCount > 0
      && Number.isSafeInteger(journal.original?.variantId) && journal.original.variantId > 0
      && journal.original.properties?._mm_item_gift === journal.offer ? journal : null;
  }
  saveSwap(cart, journal) {
    const key = swapStateKey(cart);
    if (!key) return;
    if (journal) memory.set(key, journal); else memory.delete(key);
    try {
      if (journal) this.api.storage?.setItem(key, JSON.stringify(journal));
      else if (this.api.storage?.removeItem) this.api.storage.removeItem(key);
      else this.api.storage?.setItem(key, 'null');
    } catch (_) { /* In-page recovery remains available if browser storage is blocked. */ }
  }
  async changeVerified(cart, key, quantity) {
    await this.api.waitForCart();
    let error;
    try { await this.api.change(key, quantity); } catch (caught) { error = caught; }
    const fresh = await this.read();
    if (fresh.items.some((item) => item.key === key && item.quantity > quantity)) throw error || Error('Your cart changed. Please try again.');
    return fresh;
  }
  async recoverSwap(initial, { restore = false } = {}) {
    const journal = this.swapJournal(initial);
    if (!journal) return { cart: initial, completed: false, restored: false };
    let cart = await this.cleanReserved(initial);
    let config = configsOf(cart).find((candidate) => candidate.offer === journal.offer);
    const target = () => cart.items.filter((item) => item.properties._mm_item_gift_attempt === journal.attemptId
      && item.properties._mm_item_gift === journal.offer);
    // A lost response is not a failed swap if Shopify committed a free target.
    const added = target();
    if (!restore && added.length === 1 && added[0].quantity === 1 && validGift(config, added[0])
      && Number(added[0].variant_id) === Number(journal.targetVariantId) && itemGiftPayable(added[0]) === 0
      && originalGiftCount(cart, journal) < journal.originalCount) {
      this.saveSwap(cart, null);
      return { cart, completed: true, restored: false };
    }
    // Never retain a failed replacement, even if it happened to receive a
    // discount. Remove it before restoring one original unit.
    for (const item of target()) cart = await this.changeVerified(cart, item.key, 0);
    if (target().length) throw Error('We couldn’t remove the replacement gift. Please try again.');
    cart = await this.cleanReserved(cart);
    config = configsOf(cart).find((candidate) => candidate.offer === journal.offer);
    const originalCount = originalGiftCount(cart, journal);
    let restored = originalCount >= journal.originalCount;
    if (!restored && config?.active && config.rewardAvailable && config.earned > giftCount(forOffer(cart, config))
      && availableReward(config, journal.original.variantId, journal.original.productId)) {
      try { await this.api.add(journal.original.variantId, journal.original.properties); } catch (_) { /* Verify a possible committed restore. */ }
      cart = await this.cleanReserved(await this.read());
      restored = originalGiftCount(cart, journal) >= journal.originalCount;
    }
    // A verified unavailable/paid restore should not loop on every cart event.
    // Normal reconciliation can retry the default after the usual cooldown.
    config = configsOf(cart).find((candidate) => candidate.offer === journal.offer);
    if (!restored && config?.active) {
      const view = forOffer(cart, config), stored = this.state(view);
      this.failedAttempt(view, Math.max(1, stored.data.failed?.slots || 0));
    }
    this.saveSwap(cart, null);
    return { cart, completed: false, restored };
  }
  async clean(initial) {
    let cart = initial;
    for (let count = 0; ; count++) {
      const removal = itemGiftRemovals(cart)[0];
      if (!removal) return cart;
      if (count >= 100) throw Error('Item gift cleanup did not settle');
      await this.api.waitForCart();
      // A timeout can happen after Shopify commits. Always verify the result.
      let changeError;
      try { await this.api.change(removal.key, removal.quantity); } catch (error) { changeError = error; }
      cart = await this.read();
      if (changeError && cart.items.some((item) => item.key === removal.key && item.quantity > removal.quantity)) throw changeError;
    }
  }
  async cleanReserved(initial) {
    const before = new Map();
    for (const config of configsOf(initial)) {
      const view = forOffer(initial, config);
      this.state(view); // Reserve every offer's existing gifts before any cleanup.
      before.set(config.offer, giftCount(view));
    }
    const cart = await this.clean(initial);
    for (const config of configsOf(cart)) {
      const view = forOffer(cart, config);
      const lost = Math.max(0, Math.min(before.get(config.offer) || 0, config.earned) - giftCount(view));
      if (lost && config.active) this.failedAttempt(view, lost);
    }
    return cart;
  }
  async consolidate(initial) {
    // Attempt markers belong to in-flight verification and recovery. Only
    // settled, verified-free gifts can shed them and merge with earlier units.
    if (typeof this.api.setProperties !== 'function' || this.swapJournal(initial)) return initial;
    let cart = initial;
    for (let count = 0; count < 100; count++) {
      const configs = new Map(configsOf(cart).map((config) => [config.offer, config]));
      const blocked = new Set();
      for (const config of configs.values()) {
        if (this.state(forOffer(cart, config)).data.failed) blocked.add(config.offer);
      }
      const item = cart.items.find((candidate) => isItemGift(candidate)
        && Object.prototype.hasOwnProperty.call(candidate.properties, '_mm_item_gift_attempt')
        && !blocked.has(candidate.properties._mm_item_gift)
        && validGift(configs.get(candidate.properties._mm_item_gift), candidate) && itemGiftPayable(candidate) === 0);
      if (!item) break;
      const before = settledCartUnits(cart), original = lineIdentity(item);
      await this.api.waitForCart();
      try { await this.api.setProperties(item.key, item.quantity, settledGiftProperties(item)); }
      catch (_) { /* A timed-out property rewrite can still have committed. */ }
      const fresh = await this.read();
      const preserved = settledCartUnits(fresh) === before;
      // Property updates can recalculate discounts. Never expose a gift that
      // became payable, even when the line merged successfully.
      cart = await this.cleanReserved(fresh);
      if (!preserved) throw Error('Your cart changed while updating your gifts. Please check your cart and try again.');
      // A rejected cosmetic rewrite leaves a valid free line. Retry on the next
      // refresh instead of looping or blocking checkout over separate rows.
      if (cart.items.some((candidate) => lineIdentity(candidate) === original)) break;
    }
    return cart;
  }
  async perform(allowAdd) {
    let cart = await this.read();
    const initialItems = JSON.stringify(cart.items);
    cart = (await this.recoverSwap(cart)).cart;
    cart = await this.cleanReserved(cart);
    let changed = false;
    const blocked = new Set();
    for (let attempt = 0; allowAdd && attempt < 100; attempt++) {
      let candidate;
      for (const c of configsOf(cart)) {
        const view = forOffer(cart, c), stored = this.state(view);
        if (blocked.has(c.offer) || !stored.key || !c.active || !autoAddAvailable(c) || stored.data.failed
          || stored.data.handled >= c.earned || giftCount(view) >= c.earned
          || !Number.isSafeInteger(c.variantId) || c.variantId <= 0 || !Number.isSafeInteger(c.rewardId) || c.rewardId <= 0) continue;
        candidate = { c, view, stored };
        break;
      }
      if (!candidate) break;
      const { c, view, stored } = candidate;
      stored.data.handled++;
      this.save(stored.key, stored.data); // Reserve this offer before POST, including timeouts.
      const attemptId = newAttemptId();
      this.failedAttempt(view, 1, attemptId);
      const properties = { _gift: 'true', _mm_item_gift: c.offer, _mm_item_gift_attempt: attemptId };
      let addSucceeded = false;
      try { await this.api.add(c.variantId, properties); addSucceeded = true; } catch (_) { /* Verify a possibly committed request. */ }
      cart = await this.read();
      cart = await this.cleanReserved(cart); // Never publish an owned gift with a payable price.
      changed = true;
      const current = configsOf(cart).find((config) => config.offer === c.offer);
      const added = cart.items.filter((item) => item.properties._mm_item_gift_attempt === attemptId);
      if (!current?.active || !current.rewardAvailable || added.length !== 1 || added[0].quantity !== 1
        || added[0].properties._mm_item_gift !== c.offer || Number(added[0].product_id) !== Number(current.rewardId)
        || Number(added[0].variant_id) !== Number(current.variantId) || added[0].available !== true || itemGiftPayable(added[0]) !== 0) {
        if (current) this.failedAttempt(forOffer(cart, current), 1, attemptId);
        blocked.add(c.offer);
        continue; // A rejected gift must not prevent another offer from being fulfilled.
      }
      const completed = this.state(forOffer(cart, current));
      completed.data.failed = null;
      this.save(completed.key, completed.data);
      // Presentation follows a new, verified free gift. Never wait on visuals
      // or let a visual failure change cart reconciliation or checkout safety.
      if (addSucceeded && typeof this.api.giftAdded === 'function') {
        try {
          Promise.resolve(this.api.giftAdded({
            offer: c.offer, variantId: c.variantId, rewardId: c.rewardId,
            triggerIds: Array.isArray(c.triggerIds) ? [...c.triggerIds] : [], attemptId,
          })).catch(() => {});
        } catch (_) { /* Visual confirmation is optional. */ }
      }
    }
    cart = await this.consolidate(cart);
    const previous = this.cart ? JSON.stringify(this.cart) : null;
    this.cart = cart;
    if (changed || JSON.stringify(cart.items) !== initialItems || (previous && JSON.stringify(cart) !== previous)) await this.api.notify(cart);
    return cart;
  }
  async performSwap({ offer, lineKey, variantId }) {
    if (typeof offer !== 'string' || typeof lineKey !== 'string' || !Number.isSafeInteger(Number(variantId)) || Number(variantId) <= 0) {
      throw Error('Please choose a gift and try again.');
    }
    variantId = Number(variantId);
    let cart = await this.read();
    cart = (await this.recoverSwap(cart)).cart;
    cart = await this.cleanReserved(cart);
    const config = configsOf(cart).find((candidate) => candidate.offer === offer);
    const original = cart.items.find((item) => item.key === lineKey && item.properties._mm_item_gift === offer);
    if (!original || !validGift(config, original) || itemGiftPayable(original) !== 0) {
      this.cart = cart;
      await this.api.notify(cart);
      throw Error('Your gift changed. Please reopen the choices and try again.');
    }
    if (!availableReward(config, variantId)) throw Error('That gift is no longer available. Please choose another.');
    if (Number(original.variant_id) === variantId) { this.cart = cart; return cart; }
    if (!swapStateKey(cart)) throw Error('We couldn’t check your cart. Please try again.');
    const journal = {
      version: 1, offer, attemptId: newAttemptId(), targetVariantId: variantId,
      original: { productId: Number(original.product_id), variantId: Number(original.variant_id), properties: { ...original.properties } },
      originalCount: 0,
    };
    journal.originalCount = originalGiftCount(cart, journal);
    // Persist before the first mutation. A refresh or lost network response can
    // then finish the swap or restore the original without duplicating gifts.
    this.saveSwap(cart, journal);
    let failure;
    try {
      cart = await this.changeVerified(cart, original.key, original.quantity - 1);
      const current = configsOf(cart).find((candidate) => candidate.offer === offer);
      if (originalGiftCount(cart, journal) !== journal.originalCount - 1 || !current?.active
        || !availableReward(current, variantId) || giftCount(forOffer(cart, current)) >= current.earned) {
        throw Error('Your cart changed. Please try again.');
      }
      const properties = { _gift: 'true', _mm_item_gift: offer, _mm_item_gift_attempt: journal.attemptId };
      try { await this.api.add(variantId, properties); } catch (_) { /* Confirm whether Shopify committed the replacement. */ }
      cart = await this.read();
      const result = await this.recoverSwap(cart);
      cart = result.cart;
      if (!result.completed) throw Error(result.restored
        ? 'That gift couldn’t be added for free. Your original gift is still in your cart.'
        : 'That gift is unavailable. Please check your cart and try again.');
    } catch (error) {
      failure = error;
      try {
        const result = await this.recoverSwap(await this.read(), { restore: true });
        cart = result.cart;
        if (result.restored) failure = Error('That gift couldn’t be added. Your original gift is still in your cart.');
      } catch (_) {
        // Keep the journal for the next cart refresh/checkout guard. We cannot
        // assume a failed response means that Shopify rejected the mutation.
        failure = Error('We couldn’t finish changing your gift. Please check your cart and try again.');
      }
    }
    cart = await this.consolidate(cart);
    this.cart = cart;
    // Cart sections must settle even after a rejected replacement. A rendering
    // failure cannot reverse a successfully verified gift swap.
    try { await this.api.notify(cart); } catch (error) { if (!failure) failure = Error('Your gift changed. Please refresh your cart to see it.'); }
    if (failure) throw failure;
    return cart;
  }
  swapGift(selection) {
    const job = runGiftOperation(() => this.performSwap(selection));
    this.pending = job;
    return job.finally(() => { if (this.pending === job) this.pending = null; });
  }
  refresh({ allowAdd = true } = {}) {
    // Requests that arrive during another gift controller still take their own
    // fresh snapshot once they reach the shared queue.
    const job = runGiftOperation(() => this.perform(allowAdd));
    this.pending = job;
    return job.finally(() => { if (this.pending === job) this.pending = null; });
  }
}

export async function swapItemGift(selection) {
  // A shopper action must wait outside the shared gift queue. Explicit
  // settlement refreshes still run while the cart presentation is locked.
  await waitForCartPresentation();
  if (!browserManager) return Promise.reject(Error('Your cart is still loading. Please try again.'));
  dormant = false;
  return browserManager.swapGift(selection);
}

const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;
const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
async function request(url, options = {}, type = 'json') {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
    const data = type === 'text' ? await response.text() : await response.json();
    if (!response.ok || data?.status >= 400 || data?.errors) throw Error('Cart request failed');
    return data;
  } finally { window.clearTimeout(timeout); }
}
function post(path, body) { return request(route(path), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }); }
async function waitForCart() {
  const deadline = Date.now() + 8000;
  while (document.querySelector('cart-items-component.cart-items-disabled:not([data-cart-gifts-settling])')) {
    if (Date.now() > deadline) throw Error('Cart update is still pending');
    await pause(80);
  }
}
function sectionIds() { return [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId))].slice(0, 5); }
async function notify(cart) {
  if (isCartPresentationPending()) return;
  const revision = cartPresentationRevision();
  const entries = await Promise.all(sectionIds().map(async (id) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections'); url.searchParams.delete('view'); url.searchParams.set('section_id', id);
    return [id, await request(url.toString(), { headers: { Accept: 'text/html' } }, 'text')];
  }));
  if (isCartPresentationPending() || cartPresentationRevision() !== revision) return;
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count, sections: Object.fromEntries(entries) }));
}
export async function verifyItemGiftsForCheckout() {
  await waitForCartPresentation();
  if (!browserManager) return;
  if (dormant && !document.querySelector('[data-item-gift]:not([data-item-gift=""])')) return;
  try { await browserManager.refresh(); }
  catch (error) {
    // A custom-view failure need not strand a cart with no owned item gift.
    const native = await request(route('cart.js'));
    if (!Array.isArray(native.items) || native.items.some(isItemGift)) throw error;
  }
}
function captureRemoval(event) {
  const control = event.type === 'click' ? event.target.closest?.('.cart-items__remove, [data-action="decrease"], .quantity-minus')
    : event.target.matches?.('input[name="updates[]"]') ? event.target : null;
  const row = control?.closest('[data-item-gift][data-key][data-quantity]');
  if (!row?.dataset.itemGift || control.disabled) return;
  const quantity = Number(row.dataset.quantity);
  const removed = control.matches('.cart-items__remove') ? quantity : event.type === 'click' ? 1 : quantity - Number(control.value);
  if (removed > 0) browserManager.suppress(row.dataset.itemGift, removed, row.dataset.itemGiftAttempt || '', row.dataset.key);
}
let checkoutPending = false, allowCheckout = false;
async function fallbackCheckout(event) {
  if (primaryCheckoutGuard || allowCheckout) return;
  const button = event.type === 'submit' ? event.submitter : event.target.closest?.('[name="checkout"], a[href$="/checkout"]');
  const form = event.type === 'submit' ? event.target : button?.form;
  if (event.type === 'submit' ? (button ? button.name !== 'checkout' : form?.id !== 'cart-form') : !button) return;
  event.preventDefault(); event.stopImmediatePropagation();
  if (checkoutPending) return;
  checkoutPending = true;
  button?.setAttribute('aria-busy', 'true');
  try {
    await verifyItemGiftsForCheckout();
    const nextForm = document.getElementById(form?.id || 'cart-form') || form;
    const submitter = nextForm?.querySelector('[name="checkout"]') || document.querySelector('button[name="checkout"]');
    allowCheckout = true;
    try { if (nextForm && submitter) nextForm.requestSubmit(submitter); else window.location.assign(route('checkout')); }
    finally { allowCheckout = false; }
  } catch (_) {
    for (const container of document.querySelectorAll('.cart__ctas')) {
      let error = container.querySelector('.item-gift-error');
      if (!error) { error = document.createElement('p'); error.className = 'item-gift-error'; error.setAttribute('role', 'alert'); container.prepend(error); }
      error.textContent = 'We couldn’t check your gift. Please try Checkout again, or remove the gift and retry.';
    }
  } finally { checkoutPending = false; document.querySelectorAll('[name="checkout"]').forEach((el) => el.removeAttribute('aria-busy')); }
}
export function initItemGiftManager() {
  if (window.__mmItemGiftManager) return;
  window.__mmItemGiftManager = true;
  const triggerProductIds = new Set();
  try {
    const boot = JSON.parse(document.getElementById('mm-item-gift-boot')?.textContent || '{}');
    const configs = configsOf(boot);
    dormant = configs.length > 0 && configs.every((config) => config.active === false) && boot.hasOwned === false;
    for (const config of configs) {
      if (config.active !== true || !Array.isArray(config.triggerIds)) continue;
      for (const id of config.triggerIds) {
        if (Number.isSafeInteger(id) && id > 0) triggerProductIds.add(String(id));
      }
    }
  } catch (_) { /* An absent/broken boot hint falls back to server verification. */ }
  let storage;
  try { storage = window.localStorage; } catch (_) {}
  browserManager = new ItemGiftManager({
    storage, waitForCart, notify,
    giftAdded: (detail) => document.dispatchEvent(new CustomEvent('mm:item-gift-added', { detail })),
    readCart: async () => {
      const url = new URL(route('cart?view=mm-item-gifts'), window.location.origin);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const [cart, native] = await Promise.all([request(url.toString()), request(route('cart.js'))]);
      return { ...cart, token: typeof native?.token === 'string' ? native.token : null,
        discount_codes: native?.discount_codes || [], cart_level_discount_applications: native?.cart_level_discount_applications || [] };
    },
    change: (key, quantity) => post('cart/change.js', { id: key, quantity }),
    setProperties: (key, quantity, properties) => post('cart/change.js', { id: key, quantity, properties }),
    add: (id, properties) => post('cart/add.js', { items: [{ id, quantity: 1, properties }] }),
  });
  const schedule = (event) => {
    if (suppressAutomaticGiftRefresh(event)) return;
    if (event?.detail?.data?.source === SOURCE) return;
    if (dormant) {
      const owned = event?.detail?.resource?.items?.some(isItemGift)
        || document.querySelector('[data-item-gift]:not([data-item-gift=""])');
      if (!owned) return;
      dormant = false;
    }
    window.clearTimeout(schedule.timer);
    const refresh = () => browserManager.refresh().catch((error) => console.warn('[MM item gift]', error.message));
    const data = event?.detail?.data;
    // A successful qualifying PDP add has already committed. Queue its gift
    // immediately, before unrelated debounced gift checks. This is only a
    // scheduling hint; refresh still verifies current eligibility and price.
    if (event?.type === ThemeEvents.cartUpdate && data?.source === 'product-form-component'
      && data.didError !== true && Number.isSafeInteger(data.itemDelta) && data.itemDelta > 0
      && triggerProductIds.has(String(data.productId))) {
      refresh();
      return;
    }
    schedule.timer = window.setTimeout(refresh, 200);
  };
  registerCartGiftController('item', {
    cancelScheduled: () => window.clearTimeout(schedule.timer),
    refresh: () => {
      if (dormant && !document.querySelector('[data-item-gift]:not([data-item-gift=""])')) return;
      dormant = false;
      return browserManager.refresh();
    },
  });
  document.addEventListener('click', captureRemoval, true);
  document.addEventListener('change', captureRemoval, true);
  document.addEventListener('blur', captureRemoval, true);
  document.addEventListener('click', fallbackCheckout, true);
  document.addEventListener('submit', fallbackCheckout, true);
  document.addEventListener(ThemeEvents.cartUpdate, schedule);
  document.addEventListener(ThemeEvents.discountUpdate, schedule);
  document.addEventListener(DialogOpenEvent.eventName, (event) => { if (event.target.matches?.('cart-drawer-component')) schedule(); }, true);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') schedule(); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) schedule(); });
  schedule();
}

initItemGiftManager();
